import { createSelector } from '@reduxjs/toolkit';

import type { AssetContainerModel, MediaSearchRequestIconTypeEnum, SortEnum } from 'js/src/client';
import { AssetContainerTypes, SlideBackgroundModelTypeEnum } from 'js/src/client';
import type { UploadedFile } from 'js/src/components/sketch/FileUpload';
import type { EDITOR_TAB_OPTIONS } from 'js/src/data/app';
import { getCurrentEditorTab } from 'js/src/data/app';
import type {
    CarouselMediaResults,
    FILTER_BY_LEVELS,
    MediaFoldersTable,
    MediaResultsTable,
    MediaSourceFilter,
    ModifiedAssetGenerationResponse,
    SearchContextValueType,
    StoredAssetContainer,
    StoredMediaFolder,
    StoredMediaQuerySample,
    StoredMediaResult,
} from 'js/src/data/media';
import {
    AI_MEDIA_SUB_TAB_OPTIONS,
    convertSlideBackgroundToAssetContainer,
    findGenerationInList,
    getStoredMediaFolderOrThrow,
    isDAMSearchContext,
    MEDIA_LICENSE_OPTIONS,
    MEDIA_SOURCE_OPTIONS,
    MEDIA_TYPE_OPTIONS,
    readonlySlideBackgroundModelIsImageBackgroundModel,
    readonlySlideBackgroundModelIsVideoBackgroundModel,
    RECORDING_FOLDER_NAME,
    SEARCH_CONTEXT,
} from 'js/src/data/media';
import PlainJsStoreORM from 'js/src/data/plain-js-store-orm';
import {
    readonlyAssetContainerModelIsAudio,
    readonlyAssetContainerModelIsBackground,
    readonlyAssetContainerModelIsIcon,
} from 'js/src/data/uploads';
import { withStableEmptyArray } from 'js/src/libs/consts';
import type { RootState } from 'js/src/libs/RootState';

const NO_SEARCH_TERMS: readonly string[] = withStableEmptyArray<string>([]);
const NO_CAROUSEL_RESULTS: CarouselMediaResults = {};
const NO_MEDIA_FOLDERS: readonly StoredMediaFolder[] = withStableEmptyArray<StoredMediaFolder>([]);

const videoGenerationAssets = new WeakMap<object, AssetContainerModel>();

const mediaFoldersOrm = (folders: MediaFoldersTable) =>
    new PlainJsStoreORM<StoredMediaFolder, number>(folders);

const mediaResultsOrm = (mediaResults: MediaResultsTable) =>
    new PlainJsStoreORM<StoredMediaResult, string>(mediaResults);

export function selectStoredMediaFolders(state: RootState): MediaFoldersTable {
    return state.media.all_uploads_folders;
}

export function selectStoredMediaFolder(
    state: RootState,
    folderId: number,
): StoredMediaFolder | undefined {
    return mediaFoldersOrm(selectStoredMediaFolders(state)).getById(folderId);
}

export function selectActiveStoredMediaFolder(
    state: RootState,
    searchContext: SearchContextValueType,
): StoredMediaFolder | null {
    const activeFolderId = selectActiveMediaFolder(state, searchContext);
    return activeFolderId
        ? getStoredMediaFolderOrThrow(selectStoredMediaFolders(state), activeFolderId)
        : null;
}

export function selectStoredMediaResults(
    state: RootState,
    searchContext: SearchContextValueType,
): MediaResultsTable {
    const mediaResults = userInUploadsFolder(state, searchContext)
        ? selectActiveStoredMediaFolder(state, searchContext)?.media_results
        : state.media[searchContext]?.media_results;

    if (!mediaResults) {
        throw new Error('media_results is undefined');
    }

    return mediaResults;
}

export function selectStoredMediaResultIds(
    state: RootState,
    searchContext: SearchContextValueType,
): readonly string[] {
    return selectStoredMediaResults(state, searchContext).allIds;
}

export function selectMediaQuerySearchResults(state: RootState): readonly StoredMediaQuerySample[] {
    return state.media.media_query_search_results;
}

export function selectSimilarSearchResults(state: RootState): readonly StoredAssetContainer[] {
    return state.media.similar_search_results;
}

function getActiveSourceFilters(
    state: RootState,
    searchContext: SearchContextValueType,
): MediaSourceFilter {
    const sourceFilters = userInUploadsFolder(state, searchContext)
        ? selectActiveStoredMediaFolder(state, searchContext)?.sourceFilter
        : state.media[searchContext].sourceFilter;
    if (!sourceFilters) {
        throw new Error('sourceFiltersMap is undefined');
    }
    return sourceFilters;
}

function _formatFilters(filters: MediaSourceFilter) {
    /**
     * returns a list of filters such as ['videos', 'photos', 'marketplace']
     * If an entry is missing that means that matching assets should NOT be returned
     * This is the format that MediaSearchAPIViewSet & TopUploadsTagsAPIView expects
     */
    let filterList = Object.values(MEDIA_SOURCE_OPTIONS).filter((source) => {
        return !Object.prototype.hasOwnProperty.call(filters, source) || filters[source] === true;
    });
    if (filterList.length === 0) {
        // can't have no sources, revert to all
        filterList = Object.values(MEDIA_SOURCE_OPTIONS);
    }
    return filterList;
}
function userInUploadsFolder(state: RootState, searchContext: SearchContextValueType) {
    const isInUploadVisuals = searchContext === SEARCH_CONTEXT.UPLOADED_VISUALS;
    const isInUploadIcons = searchContext === SEARCH_CONTEXT.UPLOADED_ELEMENTS;
    const isInUploadsFolder = selectActiveMediaFolder(state, searchContext) !== null; // selector returns folder_id

    return (isInUploadVisuals || isInUploadIcons) && isInUploadsFolder;
}

export const getMediaResultsORM = createSelector(
    (state: RootState, searchContext: SearchContextValueType) =>
        selectStoredMediaResults(state, searchContext),
    mediaResultsOrm,
);

export function getCarouselCategoryResults(
    state: RootState,
    searchContext: SearchContextValueType,
): CarouselMediaResults {
    return state.media[searchContext]?.carousel_media_results ?? NO_CAROUSEL_RESULTS;
}

export function getCategoryMediaResult(
    state: RootState,
    searchContext: SearchContextValueType,
    category: string,
    assetId: string,
): StoredMediaResult | null {
    const categoryData = state.media[searchContext]?.carousel_media_results[category];
    if (!categoryData) {
        return null;
    }
    return mediaResultsOrm(categoryData).getById(assetId) ?? null;
}

export function getActiveSearchTerm(state: RootState, searchContext: SearchContextValueType) {
    const searchTerm = userInUploadsFolder(state, searchContext)
        ? selectActiveStoredMediaFolder(state, searchContext)?.search_term
        : state.media[searchContext].search_term;

    if (searchTerm === undefined) {
        throw new Error('searchTerm is undefined');
    }
    return searchTerm;
}

export function getActiveCorrectedMediaSearchTerm(
    state: RootState,
    searchContext: SearchContextValueType,
) {
    return state.media[searchContext].corrected_search_term;
}

export function getActiveCorrectedMediaSearchTermIndices(
    state: RootState,
    searchContext: SearchContextValueType,
) {
    return state.media[searchContext].corrected_search_term_indices;
}

export function getActiveSuggestedMediaSearchTerm(
    state: RootState,
    searchContext: SearchContextValueType,
) {
    return state.media[searchContext].suggested_search_term;
}

export function getActiveHighlightedSuggestedMediaSearchTerm(
    state: RootState,
    searchContext: SearchContextValueType,
) {
    return state.media[searchContext].highlighted_suggested_search_term;
}

export function getActiveRelatedMediaSearchTerms(
    state: RootState,
    searchContext: SearchContextValueType,
): readonly string[] {
    return state.media[searchContext].related_search_terms;
}

export function getActiveFrequentlyUsedTags(
    state: RootState,
    searchContext: SearchContextValueType,
) {
    const folderId = selectActiveMediaFolder(state, searchContext);
    return getFrequentlyUsedTags(state, searchContext, folderId);
}

export function getFrequentlyUsedTags(
    state: RootState,
    searchContext: SearchContextValueType,
    folderId: number | null,
): readonly string[] | undefined {
    if (!folderId) {
        return state.media[searchContext].frequently_used_tags;
    }
    return getStoredMediaFolderOrThrow(selectStoredMediaFolders(state), folderId)
        .frequently_used_tags;
}

export function getActiveMediaPage(state: RootState, searchContext: SearchContextValueType) {
    const page = userInUploadsFolder(state, searchContext)
        ? selectActiveStoredMediaFolder(state, searchContext)?.page
        : state.media[searchContext].page;

    if (page === undefined) {
        throw new Error('page is undefined');
    }
    return page;
}

export function getActiveMediaPos(
    state: RootState,
    searchContext: SearchContextValueType,
): null | string {
    if (searchContext === SEARCH_CONTEXT.FREEPIK_STICKERS) {
        return state.media[searchContext].pos;
    }
    return null;
}

export function getActiveMediaNext(
    state: RootState,
    searchContext: SearchContextValueType,
): null | string {
    if (isDAMSearchContext(searchContext)) {
        return state.media[searchContext].next;
    }
    return null;
}

export function getActiveMediaFilters(
    state: RootState,
    searchContext: SearchContextValueType,
): (MEDIA_LICENSE_OPTIONS | MEDIA_SOURCE_OPTIONS | MEDIA_TYPE_OPTIONS)[] {
    if (searchContext === SEARCH_CONTEXT.FAVORITE_AI_MEDIA) {
        return [MEDIA_TYPE_OPTIONS.ALL];
    }
    if (searchContext === SEARCH_CONTEXT.AI_GENERATED_MEDIA) {
        switch (getActiveAIMediaSubTab(state)) {
            case AI_MEDIA_SUB_TAB_OPTIONS.VIDEO:
                return [MEDIA_TYPE_OPTIONS.VIDEOS];
            case AI_MEDIA_SUB_TAB_OPTIONS.IMAGE:
                return [MEDIA_TYPE_OPTIONS.PHOTOS];
            default:
                break;
        }
    }
    const sourceFilterList = _formatFilters(getActiveSourceFilters(state, searchContext));
    const selectedType =
        (userInUploadsFolder(state, searchContext)
            ? selectActiveStoredMediaFolder(state, searchContext)?.selectedType
            : state.media[searchContext].selectedType) ?? MEDIA_TYPE_OPTIONS.ALL;
    const typeFilters: MEDIA_TYPE_OPTIONS[] =
        selectedType === MEDIA_TYPE_OPTIONS.ALL
            ? [MEDIA_TYPE_OPTIONS.PHOTOS, MEDIA_TYPE_OPTIONS.VIDEOS]
            : [selectedType];
    const selectedLicense = userInUploadsFolder(state, searchContext)
        ? selectActiveStoredMediaFolder(state, searchContext)?.selectedLicense
        : state.media[searchContext].selectedLicense;
    const licenseFilters =
        selectedLicense === MEDIA_LICENSE_OPTIONS.ALL
            ? [MEDIA_LICENSE_OPTIONS.EDITORIAL, MEDIA_LICENSE_OPTIONS.STOCK]
            : [selectedLicense as MEDIA_LICENSE_OPTIONS];
    return [...sourceFilterList, ...typeFilters, ...licenseFilters];
}

export function areMediaResultsFiltered(state: RootState, searchContext: SearchContextValueType) {
    /**
     * returns a boolean indicating if results are being filtered at all (ie. 1+ filters enabled)
     */
    // first check the type radio – if it's not all, it's filtering!
    const selectedType = userInUploadsFolder(state, searchContext)
        ? selectActiveStoredMediaFolder(state, searchContext)?.selectedType
        : state.media[searchContext].selectedType;
    if (selectedType !== MEDIA_TYPE_OPTIONS.ALL) {
        return true;
    }

    // check the licensing type radio – if it's not all, it's filtering!
    const selectedLicensingType = userInUploadsFolder(state, searchContext)
        ? selectActiveStoredMediaFolder(state, searchContext)?.selectedLicense
        : state.media[searchContext].selectedLicense;
    if (selectedLicensingType !== MEDIA_LICENSE_OPTIONS.ALL) {
        return true;
    }

    // now we want to confirm that none of the source filters are checked
    const sourceFilters = getActiveSourceFilters(state, searchContext);
    const allEnabled = Object.values(sourceFilters).every((val) => val === true);
    return !allEnabled;
}

export function getActiveMediaFilterByUserLevel(
    state: RootState,
    searchContext: SearchContextValueType,
): FILTER_BY_LEVELS {
    return state.media[searchContext].filter_by_user_level;
}

export function getIsSearchInProgress(state: RootState, searchContext: SearchContextValueType) {
    return userInUploadsFolder(state, searchContext)
        ? selectActiveStoredMediaFolder(state, searchContext)?.isSearchInProgress
        : state.media[searchContext].isSearchInProgress;
}

export function getActiveMediaMoreResults(state: RootState, searchContext: SearchContextValueType) {
    return userInUploadsFolder(state, searchContext)
        ? selectActiveStoredMediaFolder(state, searchContext)?.more_results
        : state.media[searchContext].more_results;
}

export function getActiveMediaLastSearchedTerm(
    state: RootState,
    searchContext: SearchContextValueType,
) {
    return state.media[searchContext].last_searched_term;
}

export function getLastUsedFilters(state: RootState, searchContext: SearchContextValueType) {
    return state.media[searchContext].last_used_filters;
}

export function getActiveMediaNoAccess(state: RootState, searchContext: SearchContextValueType) {
    return state.media[searchContext].no_access;
}

export function getActiveMediaNoResults(state: RootState, searchContext: SearchContextValueType) {
    return userInUploadsFolder(state, searchContext)
        ? selectActiveStoredMediaFolder(state, searchContext)?.no_results
        : state.media[searchContext].no_results;
}

export function getActiveMediaSourceFilterValue(
    state: RootState,
    searchContext: SearchContextValueType,
    filter_key: MEDIA_SOURCE_OPTIONS,
) {
    return userInUploadsFolder(state, searchContext)
        ? selectActiveStoredMediaFolder(state, searchContext)?.sourceFilter[
              filter_key as MEDIA_SOURCE_OPTIONS
          ] !== false
        : state.media[searchContext].sourceFilter[filter_key as MEDIA_SOURCE_OPTIONS] !== false;
}

export function getActiveMediaTypeFilterChecked(
    state: RootState,
    searchContext: SearchContextValueType,
    filter_key: MEDIA_TYPE_OPTIONS,
) {
    return userInUploadsFolder(state, searchContext)
        ? selectActiveStoredMediaFolder(state, searchContext)?.selectedType === filter_key
        : state.media[searchContext].selectedType === filter_key;
}

export function getActiveMediaLicenseFilterChecked(
    state: RootState,
    searchContext: SearchContextValueType,
    license_key: MEDIA_LICENSE_OPTIONS,
) {
    return userInUploadsFolder(state, searchContext)
        ? selectActiveStoredMediaFolder(state, searchContext)?.selectedLicense === license_key
        : state.media[searchContext].selectedLicense === license_key;
}

export function getActiveStickerType(
    state: RootState,
    searchContext: SearchContextValueType,
): MediaSearchRequestIconTypeEnum {
    return state.media[searchContext].selectedStickerType;
}

export function getActiveMediaSort(
    state: RootState,
    searchContext: SearchContextValueType,
): SortEnum | null {
    return userInUploadsFolder(state, searchContext)
        ? getActiveFolderSort(state, searchContext)
        : state.media[searchContext].sort;
}

export const getMediaIdsPrioritizedWithCurrentSessionUploads = createSelector(
    /*
    This selector returns all the media_ids in the section of the media library specified by searchContext.
    If we are in an uploads searchContext, some search results could have `current_session_upload = true`.
    We move those to the front of the list, so they are displayed first in the UI.
    */
    selectStoredMediaResults,
    (mediaResults): readonly string[] => {
        const orm = mediaResultsOrm(mediaResults);
        const currentSessionUploadsIds = orm
            .filter((media) => media.current_session_upload === true)
            .map((media) => media._id);
        const previouslyUploadedIds = orm
            .filter((media) => media.current_session_upload === undefined)
            .map((media) => media._id);
        return [...currentSessionUploadsIds, ...previouslyUploadedIds];
    },
);

export function getMediaResult(
    state: RootState,
    searchContext: SearchContextValueType,
    mediaId: string | undefined,
): StoredMediaResult | undefined {
    if (mediaId === undefined) {
        return undefined;
    }
    return getMediaResultsORM(state, searchContext).getById(mediaId);
}

function getStoredAssetField<Field extends 'source' | 'uuid'>(
    asset: StoredMediaResult,
    field: Field,
) {
    if (readonlyAssetContainerModelIsAudio(asset)) {
        return asset[AssetContainerTypes.AUDIO][field];
    }
    if (readonlyAssetContainerModelIsBackground(asset)) {
        const slideBackground = asset[AssetContainerTypes.BACKGROUND];
        if (readonlySlideBackgroundModelIsImageBackgroundModel(slideBackground)) {
            return slideBackground[SlideBackgroundModelTypeEnum.IMAGE][field];
        } else if (readonlySlideBackgroundModelIsVideoBackgroundModel(slideBackground)) {
            return slideBackground[SlideBackgroundModelTypeEnum.VIDEO][field];
        }
    }
    if (readonlyAssetContainerModelIsIcon(asset)) {
        return asset[AssetContainerTypes.ICON][field];
    }
    throw new Error('unknown asset container type!');
}

export function getMediaSourceById(
    state: RootState,
    searchContext: SearchContextValueType,
    id: string,
) {
    return getStoredAssetField(
        getMediaResultsORM(state, searchContext).getByIdOrThrow(id),
        'source',
    );
}

export function getMediaResultAssetId(
    state: RootState,
    searchContext: SearchContextValueType,
    mediaId: string,
) {
    return getStoredAssetField(
        getMediaResultsORM(state, searchContext).getByIdOrThrow(mediaId),
        'uuid',
    );
}

export function getMediaResultCount(state: RootState, searchContext: SearchContextValueType) {
    const orm = getMediaResultsORM(state, searchContext);
    return orm.length;
}

/**
 * Returns the current SEARCH_CONTEXT ('uploaded-audio', etc) for the currently selected EDITOR_TAB ('music', etc)
 * If the currently selected tab is NOT a search tab (Format, Style, etc) we'll return `undefined`.
 * NOTE: This should only be used when we're _actually_ looking for the currently selected tab's search context.
 *       In most components we want them to not re-render when the selected tab changes (ie. <AudioAsset> shouldn't
 *       update when we switch between the Icons & Format tabs). So you'll likely want to use
 *       `getActiveSearchContextForTab()` instead
 * @returns SEARCH_CONTEXT
 */
export function getActiveSearchContextForCurrentTab(
    state: RootState,
): SearchContextValueType | undefined {
    const editorTab = getCurrentEditorTab(state);
    return getActiveSearchContextForTab(state, editorTab);
}

/**
 * Returns the current SEARCH_CONTEXT ('uploaded-audio', etc) for the provided EDITOR_TAB ('music', etc)
 * @returns SEARCH_CONTEXT
 */
export function getActiveSearchContextForTab(
    state: RootState,
    editorTab: EDITOR_TAB_OPTIONS | null,
): SearchContextValueType {
    // Callers rely on a defined context; a null or unmapped tab yields undefined at runtime.
    return (
        editorTab === null ? undefined : state.media.search_context[editorTab]
    ) as SearchContextValueType;
}

export function getRecommendedSearchTermsForSearchContext(
    state: RootState,
    searchContext: SearchContextValueType,
) {
    const categoriesWithoutRecommendedSearchTerms = [
        SEARCH_CONTEXT.STOCK_AUDIO,
        SEARCH_CONTEXT.UPLOADED_AUDIO,
        SEARCH_CONTEXT.RECENTS_AUDIO,
        SEARCH_CONTEXT.FAVORITE_AUDIO,
    ];
    if (
        categoriesWithoutRecommendedSearchTerms.includes(
            searchContext as (typeof categoriesWithoutRecommendedSearchTerms)[number],
        )
    ) {
        return NO_SEARCH_TERMS;
    }
    return state.undoable.present.media.recommended_search_terms;
}

export function getIsFetchingRecommendedSearchTerms(state: RootState) {
    return state.undoable.present.media.is_fetching_recommended_search_terms;
}

export function getRecommendedSearchTerms(state: RootState, searchContext: SearchContextValueType) {
    return getRecommendedSearchTermsForSearchContext(state, searchContext);
}

export function getAllFavoriteMedia(state: RootState) {
    return state.media.all_favorite_media;
}

export function isMediaInFavourites(
    allFavoriteMedia: ReturnType<typeof getAllFavoriteMedia>,
    media_id: string | null,
) {
    return media_id !== null && new PlainJsStoreORM(allFavoriteMedia).contains(media_id);
}

export function getIsFavouriteMedia(state: RootState, media_id: string | null) {
    return isMediaInFavourites(getAllFavoriteMedia(state), media_id);
}

export function getPlayingTrackUrl(state: RootState) {
    return state.media.playingAudioDetails.url;
}

export function getPlayingTrackSeekPercent(state: RootState) {
    return state.media.playingAudioDetails.seekPercent;
}

export function getPlayingTrackScrubberSeekPercent(state: RootState) {
    return state.media.playingAudioDetails.scrubberSeekPercent;
}

export function getPlayingTrackDuration(state: RootState) {
    return state.media.playingAudioDetails.duration;
}

export function getPlayingTrackStatus(state: RootState) {
    return state.media.playingAudioDetails.isPlaying;
}

export function hasFolders(state: RootState) {
    return !!state.media?.all_uploads_folders;
}

const findStoredFolderByName = (state: RootState, folderName: string) =>
    mediaFoldersOrm(selectStoredMediaFolders(state)).find((folder) => folder.name === folderName);

export function selectRecordingFolder(state: RootState, name: string = RECORDING_FOLDER_NAME) {
    if (hasFolders(state)) {
        return findStoredFolderByName(state, name);
    }
}

export function selectFolderByName(state: RootState, folderName: string) {
    if (hasFolders(state)) {
        return findStoredFolderByName(state, folderName);
    }
}

export const selectAllFolders = createSelector(
    selectStoredMediaFolders,
    (allFolders): readonly StoredMediaFolder[] =>
        allFolders ? mediaFoldersOrm(allFolders).map((folder) => folder) : NO_MEDIA_FOLDERS,
);

export function selectActiveMediaFolder(
    state: RootState,
    searchContext: SearchContextValueType | null,
): number | null {
    if (hasFolders(state) && searchContext) {
        return state.media.active_uploads_folder[searchContext] ?? null;
    } else {
        return null;
    }
}

export function getActiveFolderDetails(
    state: RootState,
    searchContext: SearchContextValueType,
): StoredMediaFolder | null {
    if (hasFolders(state)) {
        return selectActiveStoredMediaFolder(state, searchContext);
    }
    return null;
}

export function getActiveFolderSort(state: RootState, searchContext: SearchContextValueType) {
    if (hasFolders(state)) {
        return selectActiveStoredMediaFolder(state, searchContext)?.sort ?? null;
    }
    return null;
}

export function getFolderDetailsById(
    state: RootState,
    folderId: number | null,
): StoredMediaFolder | null {
    if (hasFolders(state) && folderId) {
        return getStoredMediaFolderOrThrow(selectStoredMediaFolders(state), folderId);
    }
    return null;
}

export function getFolderDetailsByNameAndSource(
    state: RootState,
    folderName: string,
    creationSource: SearchContextValueType,
): StoredMediaFolder | null {
    if (hasFolders(state)) {
        return (
            mediaFoldersOrm(selectStoredMediaFolders(state)).find(
                (each) => each.name === folderName && each.creation_source === creationSource,
            ) ?? null
        );
    }
    return null;
}

export function getMediaQueryResultByIndex(
    state: RootState,
    queryIndex: number,
    mediaIndex: number,
): StoredAssetContainer | null {
    return selectMediaQuerySearchResults(state)[queryIndex]?.results[mediaIndex] ?? null;
}

export function getSimilarSearchResultByIndex(
    state: RootState,
    mediaIndex: number,
    isAIContext = false,
): StoredAssetContainer | undefined {
    if (isAIContext) {
        return state.media.ai_similar_generations[mediaIndex]?.result.asset;
    }
    return selectSimilarSearchResults(state)[mediaIndex];
}

export function getIsSimilarSearchContext(state: RootState) {
    return state.media.is_similar_search_context;
}

export function getSimilarSearchOriginalAsset(state: RootState): StoredAssetContainer | null {
    return state.media.similar_search_original_asset;
}

export function getSimilarSearchOriginalAssetUuid(state: RootState) {
    const background = getSimilarSearchOriginalAsset(state)?.[AssetContainerTypes.BACKGROUND];
    if (background) {
        if (readonlySlideBackgroundModelIsVideoBackgroundModel(background)) {
            return background[SlideBackgroundModelTypeEnum.VIDEO].uuid || null;
        } else if (readonlySlideBackgroundModelIsImageBackgroundModel(background)) {
            return background[SlideBackgroundModelTypeEnum.IMAGE].uuid || null;
        }
    }
    return null;
}

export function getCurrentMediaGenerations(state: RootState) {
    return state.media.current_media_generations;
}

export function getPreviousMediaGenerations(state: RootState) {
    return state.media.previous_media_generations;
}

export function getGenerationFromMediaUuid(
    state: RootState,
    mediaUuid: string,
    mediaType: SlideBackgroundModelTypeEnum,
) {
    // First check the current and previous generations list used in the AI Media tab
    const currentAndPreviousGenerations = [
        ...getCurrentMediaGenerations(state),
        ...getPreviousMediaGenerations(state),
    ];

    const foundinAIMediaList = findGenerationInList(
        currentAndPreviousGenerations,
        mediaUuid,
        mediaType,
    );
    if (foundinAIMediaList) return foundinAIMediaList;

    // We haven't found the generation in the AI Media tab, so check the similar generations list
    const foundInSimilar = findGenerationInList(
        getAISimilarGenerations(state),
        mediaUuid,
        mediaType,
    );
    if (foundInSimilar) return foundInSimilar;

    return null;
}

export function getIsSavingAIMediaForGeneration(state: RootState, generationUuid: string): boolean {
    const generations = [
        ...getCurrentMediaGenerations(state),
        ...getPreviousMediaGenerations(state),
    ];
    const generation = generations.find((g) => g?.result.uuid === generationUuid);
    return generation?.result.isSaving || false;
}

export function getAISimilarGenerations(state: RootState): ModifiedAssetGenerationResponse[] {
    return state.media.ai_similar_generations;
}

export function getSelectedReferenceImage(state: RootState): UploadedFile | null {
    return state.media.selected_reference_image;
}

export function getSelectedReferenceVideo(state: RootState) {
    return state.media.selected_reference_video;
}

export function getMediaErrors(state: RootState) {
    return state.media?.media_errors;
}

export function getActiveAudioDurationFilter(
    state: RootState,
    searchContext: SearchContextValueType,
) {
    return state.media[searchContext].audioDurationFilter;
}

export const getSearchContextFolders = createSelector(
    selectAllFolders,
    (_: RootState, searchContext: SearchContextValueType) => searchContext,
    (allFolders, searchContext) => {
        return allFolders.filter((folder) => folder.creation_source === searchContext);
    },
);

export const getNonEmptyFolders = createSelector(getSearchContextFolders, (folders) => {
    return folders.filter((folder) => folder.media_count > 0);
});

export const getTalkingHeadMediaResults = createSelector(getMediaResultsORM, (mediaResultORM) =>
    mediaResultORM.filter((media) => {
        if (!readonlyAssetContainerModelIsBackground(media)) {
            return false;
        }
        const background = media[AssetContainerTypes.BACKGROUND];
        if (!readonlySlideBackgroundModelIsVideoBackgroundModel(background)) {
            return false;
        }
        return !!background[SlideBackgroundModelTypeEnum.VIDEO]?.has_voiceover;
    }),
);

export const getVoiceoverMediaResults = createSelector(getMediaResultsORM, (mediaResultORM) =>
    mediaResultORM.filter((media) => readonlyAssetContainerModelIsAudio(media)),
);

export function getAIMediaModels(state: RootState) {
    return state.media.ai_media_models;
}

export function getHasVideoToVideoModels(state: RootState): boolean {
    return state.media.ai_media_models.some((m) => m.model_type === 'video_to_video');
}

export function getVideoGenerationTasks(state: RootState) {
    return state.media.video_generation_tasks;
}

export function getVideoGenerationAssetData(
    state: RootState,
    taskId: string,
): AssetContainerModel | null {
    const result = getVideoGenerationTasks(state)[taskId]?.result;
    if (!result) {
        return null;
    }
    const cached = videoGenerationAssets.get(result);
    if (cached) {
        return cached;
    }
    const asset = convertSlideBackgroundToAssetContainer(result);
    videoGenerationAssets.set(result, asset);
    return asset;
}

export function getIsInitializingVideoGeneration(state: RootState) {
    return state.media.is_initializing_video_generation;
}

export function getIsInitializingImageGeneration(state: RootState) {
    return state.media.is_initializing_image_generation;
}

export function getActiveAIMediaSubTab(state: RootState) {
    return state.media.active_ai_media_sub_tab;
}

export function getUserSelectedGenMediaAspectRatio(state: RootState): string | null {
    return state.media.user_selected_gen_media_aspect_ratio;
}
